import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Validator from "components/Validator/Validator.component";
import { secureTrim } from "helpers/string";
import sanitizeHtml from "sanitize-html";
import { useTypedSelector } from "store";
import { editTableAction } from "store/reducers/products/tables/tables.actions";
import colors from "theme/colors";
import { RuleTableResponse, TablesTypeEnum } from "types/table";
import { ObjectAssignedEnum, ValidationResponse } from "types/validation";
import { getTableMetaData } from "./helpers";
import { RulesTableSettingsInput } from "./RulesTableSettingsInput.component";

const FLUID_META_PANEL_MIN_WIDTH_PX = 280;
/** Default single-line height shared by left settings rows and empty Comments/Sources. */
const META_SETTINGS_ROW_HEIGHT_PX = 36;
/** Comments / Sources grow with content up to this, then scroll — left table unaffected. */
const META_CONTENT_MAX_HEIGHT_PX = 120;
/** Expanded Comments / Sources max height (~4× collapsed). */
const META_CONTENT_EXPANDED_MAX_HEIGHT_PX = 480;
/**
 * Fixed left value-column width so Rulesets chips wrap inside a stable box
 * instead of resizing the table on every select/deselect.
 * Matches SearchableDropdown's Autocomplete max-width.
 */
const META_LEFT_VALUE_WIDTH_PX = 400;

const labelCellSx = {
  backgroundColor: colors.tables.columnsAndRowsTitles,
  color: colors.primary,
  fontWeight: 700,
  fontSize: 14,
  whiteSpace: "nowrap" as const,
  // 1% + nowrap: column shrinks to the widest caption, no extra horizontal space.
  // <br /> still splits "Validation Issues"; nowrap only blocks soft wrapping.
  width: "1%",
  px: 2,
  py: 0,
  border: `1px solid ${colors.tables.borders}`,
  verticalAlign: "middle" as const,
  lineHeight: 1.25,
};

const valueCellSx = {
  backgroundColor: colors.white,
  fontSize: 14,
  px: 2,
  py: 0,
  border: `1px solid ${colors.tables.borders}`,
  verticalAlign: "middle" as const,
};

const readOnlyValueCellSx = {
  ...valueCellSx,
  backgroundColor: colors.background.secondaryLabels,
  width: "100%",
  minWidth: 0,
  overflow: "hidden" as const,
};

const ownerToolWarningValueCellSx = {
  ...valueCellSx,
  backgroundColor: colors.background.primaryLabelsOrange,
  // maxWidth 0: wrap to the value-column width set by other rows (don't expand table).
  maxWidth: 0,
  whiteSpace: "normal" as const,
  overflowWrap: "anywhere" as const,
  wordBreak: "break-word" as const,
  overflowY: "auto" as const,
};

const scrollableContentSx = {
  // Fill the default row when empty so right table matches left row height.
  minHeight: META_SETTINGS_ROW_HEIGHT_PX,
  maxHeight: META_CONTENT_MAX_HEIGHT_PX,
  overflowY: "auto" as const,
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box" as const,
  display: "flex",
};

const editableFieldSx = {
  width: "100%",
  outline: "none",
  cursor: "text",
  lineHeight: 1.4,
  whiteSpace: "normal" as const,
  overflowWrap: "anywhere" as const,
  wordBreak: "break-word" as const,
  "&:empty::before": {
    content: '" "',
    display: "inline-block",
  },
};

/** Table Type / Lookup: fixed default single-line height. */
const settingsMetaRowSx = {
  height: META_SETTINGS_ROW_HEIGHT_PX,
  maxHeight: META_SETTINGS_ROW_HEIGHT_PX,
  "& > .MuiTableCell-root": {
    height: META_SETTINGS_ROW_HEIGHT_PX,
    maxHeight: META_SETTINGS_ROW_HEIGHT_PX,
    boxSizing: "border-box" as const,
    overflow: "hidden" as const,
  },
};

/** Rulesets: same default height as Table Type / Lookup; may grow when chips wrap. */
const rulesetsMetaRowSx = {
  height: META_SETTINGS_ROW_HEIGHT_PX,
  minHeight: META_SETTINGS_ROW_HEIGHT_PX,
  "& > .MuiTableCell-root": {
    height: META_SETTINGS_ROW_HEIGHT_PX,
    minHeight: META_SETTINGS_ROW_HEIGHT_PX,
    boxSizing: "border-box" as const,
    verticalAlign: "middle" as const,
  },
};

/**
 * Comments / Sources rows size to their scrollable content.
 * Empty state matches left rows via scrollableContentSx.minHeight — not a shared table row.
 */
const commentsSourcesMetaRowSx = {
  "& > .MuiTableCell-root": {
    boxSizing: "border-box" as const,
    verticalAlign: "middle" as const,
  },
};

const metaTableSx = {
  width: "auto",
  flexShrink: 0,
  borderCollapse: "collapse" as const,
  "& td": {
    borderCollapse: "collapse",
  },
};

const fluidMetaTableSx = {
  width: "100%",
  // auto (not fixed): label 1%+nowrap can shrink-to-fit like the left meta table.
  tableLayout: "auto" as const,
  borderCollapse: "collapse" as const,
  "& td": {
    borderCollapse: "collapse",
  },
};

const fluidMetaPanelSx = {
  flex: "1 1 0",
  width: 0,
  minWidth: FLUID_META_PANEL_MIN_WIDTH_PX,
};

const scrollableValueCellSx = {
  ...valueCellSx,
  width: "100%",
  minWidth: 0,
  overflow: "hidden" as const,
};

/** Left settings values: fixed width so chip add/remove doesn't resize the table. */
const leftSettingsValueCellSx = {
  ...valueCellSx,
  width: META_LEFT_VALUE_WIDTH_PX,
  minWidth: META_LEFT_VALUE_WIDTH_PX,
  maxWidth: META_LEFT_VALUE_WIDTH_PX,
  overflow: "hidden" as const,
};

export type RulesTableHeaderProps = {
  readonly ruleTableData: RuleTableResponse;
  readonly hideCommentsAndSources?: boolean;
  readonly readOnly?: boolean;
};

function MetaLabelCell({ children }: { readonly children: React.ReactNode }) {
  return <TableCell sx={labelCellSx}>{children}</TableCell>;
}

function MetaLabelCellWithExpand({
  children,
  expanded,
  onToggle,
}: {
  readonly children: React.ReactNode;
  readonly expanded: boolean;
  readonly onToggle: () => void;
}) {
  const label = expanded ? "Collapse" : "Expand";
  return (
    <TableCell sx={labelCellSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.5,
          width: "100%",
        }}
      >
        <span>{children}</span>
        <Tooltip title={label}>
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label={label}
            aria-expanded={expanded}
            sx={{ p: 0.25 }}
          >
            {expanded ? (
              <UnfoldLessIcon sx={{ fontSize: 16 }} />
            ) : (
              <UnfoldMoreIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </TableCell>
  );
}

function MetaValueCell({
  children,
  scrollable = false,
  readOnly = false,
  expanded = false,
}: {
  readonly children: React.ReactNode;
  readonly scrollable?: boolean;
  /** Shaded background for non-editable cells (e.g. Validation Issues). */
  readonly readOnly?: boolean;
  /** Comments / Sources: taller scroll viewport when expanded. */
  readonly expanded?: boolean;
}) {
  let cellSx:
    | typeof leftSettingsValueCellSx
    | typeof readOnlyValueCellSx
    | typeof scrollableValueCellSx = leftSettingsValueCellSx;
  if (readOnly) {
    cellSx = readOnlyValueCellSx;
  } else if (scrollable) {
    cellSx = scrollableValueCellSx;
  }

  return (
    <TableCell sx={cellSx}>
      {scrollable ? (
        <ScrollableMetaContent expanded={expanded}>
          {children}
        </ScrollableMetaContent>
      ) : (
        children
      )}
    </TableCell>
  );
}

/**
 * Vertically centers short Comments/Sources content; switches to flex-start
 * when content overflows so the scrollbar can reach the first line.
 */
function ScrollableMetaContent({
  expanded,
  children,
}: {
  readonly expanded: boolean;
  readonly children: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      // Subpixel tolerance — avoid flicker when heights are nearly equal.
      const next = el.scrollHeight > el.clientHeight + 1;
      setOverflowing((prev) => (prev === next ? prev : next));
    };

    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    const child = el.firstElementChild;
    if (child) {
      resizeObserver.observe(child);
    }

    // contentEditable typing changes DOM without always resizing the box.
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [expanded]);

  useEffect(() => {
    if (overflowing && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [overflowing]);

  return (
    <Box
      ref={containerRef}
      sx={{
        ...scrollableContentSx,
        maxHeight: expanded
          ? META_CONTENT_EXPANDED_MAX_HEIGHT_PX
          : META_CONTENT_MAX_HEIGHT_PX,
        alignItems: overflowing ? "flex-start" : "center",
      }}
    >
      {children}
    </Box>
  );
}

function EditableHtmlCell({
  html,
  onSave,
  resetKey,
  readOnly = false,
}: {
  readonly html: string | undefined;
  readonly onSave: (nextHtml: string) => void;
  /** Remount / resync when switching tables — not on every save echo. */
  readonly resetKey: string;
  readonly readOnly?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = html || "";
    }
    // Intentionally only when the table (or field identity) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (readOnly) {
    const trimmed = html?.trim();
    if (!trimmed) return null;
    return (
      <Box
        sx={{ ...editableFieldSx, cursor: "default" }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(trimmed) }}
      />
    );
  }

  return (
    <Box
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      sx={editableFieldSx}
      onBlur={() => {
        onSave(sanitizeHtml(secureTrim(ref.current?.innerHTML ?? "")));
      }}
    />
  );
}

export function RulesTableHeader({
  ruleTableData,
  hideCommentsAndSources = false,
  readOnly = false,
}: RulesTableHeaderProps) {
  const dispatch = useDispatch();
  const ruleSetsList =
    useTypedSelector((state) => state.products?.parameters.ruleSets) || [];
  const metaDropdownData = useMemo(
    () => getTableMetaData(ruleTableData.rulesets, ruleTableData, ruleSetsList),
    [ruleTableData, ruleSetsList]
  );
  const isLookup = [TablesTypeEnum.ATTR_LOOKUP, TablesTypeEnum.LOOKUP].includes(
    ruleTableData.tableType as TablesTypeEnum
  );
  const showOwnerTool = Boolean(ruleTableData.ownerTool);
  const validations =
    (ruleTableData.validations as ValidationResponse[] | undefined) ?? [];
  const showValidationIssues = validations.length > 0;
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);

  const saveComments = (comments: string) => {
    if (readOnly || !ruleTableData.id) return;
    dispatch(
      editTableAction({
        tableId: ruleTableData.id,
        updateData: { comments },
      })
    );
  };

  const saveSources = (sources: string) => {
    if (readOnly || !ruleTableData.id) return;
    dispatch(
      editTableAction({
        tableId: ruleTableData.id,
        updateData: { sources },
      })
    );
  };

  const tableValidator = (
    <Validator
      objectType={ObjectAssignedEnum.RULETABLE}
      objectData={ruleTableData}
      validations={validations}
    />
  );

  return (
    <Stack spacing={1.5} sx={{ width: "100%", mb: 1 }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        sx={{ width: "100%" }}
      >
        <Table size="small" sx={metaTableSx}>
          <TableBody>
            <TableRow sx={settingsMetaRowSx}>
              <MetaLabelCell>TABLE TYPE</MetaLabelCell>
              <MetaValueCell>
                <RulesTableSettingsInput
                  rulesTableParameterType="tableType"
                  options={metaDropdownData.tableTypeOptions}
                  currentValue={metaDropdownData.currentTableType}
                  placeholder="select table type"
                  multiselect={false}
                  ruleTableData={ruleTableData}
                  readOnly={readOnly}
                />
              </MetaValueCell>
            </TableRow>

            {isLookup && (
              <TableRow sx={settingsMetaRowSx}>
                <MetaLabelCell>LOOKUP PROPERTIES</MetaLabelCell>
                <MetaValueCell>
                  <RulesTableSettingsInput
                    rulesTableParameterType="lookUpFieldIndex"
                    currentValue={ruleTableData.lookUpFieldIndex}
                    ruleTableData={ruleTableData}
                    readOnly={readOnly}
                  />
                </MetaValueCell>
              </TableRow>
            )}

            <TableRow sx={rulesetsMetaRowSx}>
              <MetaLabelCell>RULESETS</MetaLabelCell>
              <MetaValueCell>
                <RulesTableSettingsInput
                  rulesTableParameterType="rulesetIds"
                  options={metaDropdownData.rulesetOptions}
                  currentValue={metaDropdownData.currentRulesets}
                  placeholder="rulesets"
                  multiselect={true}
                  ruleTableData={ruleTableData}
                  readOnly={readOnly}
                />
              </MetaValueCell>
            </TableRow>

            {showOwnerTool && (
              <TableRow sx={rulesetsMetaRowSx}>
                <TableCell sx={labelCellSx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <span>OWNER TOOL</span>
                    <WarningAmberOutlinedIcon
                      color="warning"
                      fontSize="small"
                      aria-label="Warning"
                    />
                  </Box>
                </TableCell>
                <TableCell sx={ownerToolWarningValueCellSx}>
                  {ruleTableData.ownerTool} owns this table. Any edits may be
                  overwritten.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {!hideCommentsAndSources && (
          <Box sx={fluidMetaPanelSx}>
            <Table size="small" sx={fluidMetaTableSx}>
              <TableBody>
                <TableRow sx={commentsSourcesMetaRowSx}>
                  <MetaLabelCellWithExpand
                    expanded={commentsExpanded}
                    onToggle={() => setCommentsExpanded((prev) => !prev)}
                  >
                    COMMENTS
                  </MetaLabelCellWithExpand>
                  <MetaValueCell
                    scrollable
                    expanded={commentsExpanded}
                    readOnly={readOnly}
                  >
                    <EditableHtmlCell
                      html={ruleTableData.comments}
                      onSave={saveComments}
                      resetKey={`${ruleTableData.id}-comments`}
                      readOnly={readOnly}
                    />
                  </MetaValueCell>
                </TableRow>
                <TableRow sx={commentsSourcesMetaRowSx}>
                  <MetaLabelCellWithExpand
                    expanded={sourcesExpanded}
                    onToggle={() => setSourcesExpanded((prev) => !prev)}
                  >
                    SOURCES
                  </MetaLabelCellWithExpand>
                  <MetaValueCell
                    scrollable
                    expanded={sourcesExpanded}
                    readOnly={readOnly}
                  >
                    <EditableHtmlCell
                      html={ruleTableData.sources}
                      onSave={saveSources}
                      resetKey={`${ruleTableData.id}-sources`}
                      readOnly={readOnly}
                    />
                  </MetaValueCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}
      </Stack>

      {showValidationIssues ? (
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <Table size="small" sx={fluidMetaTableSx}>
            <TableBody>
              <TableRow>
                <MetaLabelCell>
                  VALIDATION
                  <br />
                  ISSUES
                </MetaLabelCell>
                <MetaValueCell readOnly>{tableValidator}</MetaValueCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      ) : (
        // Keep Validator mounted so fetch/dispatch side-effects still run.
        <Box sx={{ display: "none" }} aria-hidden>
          {tableValidator}
        </Box>
      )}
    </Stack>
  );
}

export default RulesTableHeader;
